import React from 'react';
import { GripVertical, User, MoonStar, Lock, Users, Link2, AlertOctagon, StickyNote } from 'lucide-react';
import { getStatusStyle } from './calendarUtils';
import { ShiftPeriodBadgeForShift } from './ShiftPeriodBadge';
import { cardIndicators } from './cardIndicators';

/** UC-B-1 (UI-16): segment colour bar · group chip · open-coverage text · overlap mark · lock — from S2–S4 board fields only. */
export function CardIndicators({ shift, onGroup, dense = false }) {
  const ind = cardIndicators(shift);
  return (
    <>
      {ind.segments.length > 0 && (
        <div className="relative h-1 w-full rounded bg-slate-200/70 overflow-hidden mb-0.5" data-testid={`card-segment-bar-${shift.id}`}>
          {ind.segments.map((b, i) => <span key={i} className={`absolute inset-y-0 ${b.colour}`} style={{ left: `${b.left}%`, width: `${b.width}%` }} title={b.title} />)}
        </div>
      )}
      <div className="flex items-center gap-1 flex-wrap">
        {ind.group && (
          <button type="button" onClick={(e) => { e.stopPropagation(); onGroup && onGroup(shift, { openGroup: true }); }} title={ind.group.title}
            className="inline-flex items-center gap-0.5 rounded bg-sky-100 text-sky-800 border border-sky-200 px-1 text-[9px] font-medium hover:bg-sky-200" data-testid={`card-group-chip-${shift.id}`}>
            <Users className="w-2.5 h-2.5" /> {ind.group.label}{ind.group.count ? ` · ${ind.group.count}` : ''}
          </button>
        )}
        {ind.overlap && (
          <span title={ind.overlap.title} data-testid={`card-overlap-${ind.overlap.kind.toLowerCase()}-${shift.id}`}
            className={`inline-flex items-center gap-0.5 rounded px-1 text-[9px] font-medium ${ind.overlap.kind === 'CONFLICT' ? 'bg-red-100 text-red-800 border border-red-300' : 'bg-violet-100 text-violet-800 border border-violet-200'}`}>
            {ind.overlap.kind === 'CONFLICT' ? <AlertOctagon className="w-2.5 h-2.5" /> : <Link2 className="w-2.5 h-2.5" />}{dense ? '' : ` ${ind.overlap.label}`}
          </span>
        )}
        {ind.locked && <Lock className="w-2.5 h-2.5 text-amber-700" title={ind.locked.title} data-testid={`card-lock-${shift.id}`} />}
        {ind.open && !dense && <span className="text-[9px] font-semibold text-amber-700" data-testid={`card-open-${shift.id}`}>{ind.open}</span>}
      </div>
    </>
  );
}

/** client#3: assigned staff + shift notes, shown on every board card (compact ones too). */
export function CardStaffNotes({ shift }) {
  const staff = (shift.assigned_staff || []).map(s => s.name).filter(Boolean).join(', ');
  const notes = (shift.notes || '').trim();
  return (
    <>
      {staff ? (
        <div className="flex items-center gap-1 mt-0.5 min-w-0" title={staff}>
          <User className="w-2.5 h-2.5 text-slate-500 flex-shrink-0" />
          <span className="text-[10px] font-medium text-slate-700 truncate" data-testid={`card-staff-${shift.id}`}>{staff}</span>
        </div>
      ) : !shift.shared_group_id && (   // shared-group cover lives on the group, not the member shift
        <div className="flex items-center gap-1 mt-0.5 min-w-0">
          <User className="w-2.5 h-2.5 text-amber-600 flex-shrink-0" />
          <span className="text-[10px] font-medium text-amber-700 truncate" data-testid={`card-staff-${shift.id}`}>Unassigned</span>
        </div>
      )}
      {notes && (
        <div className="flex items-center gap-1 mt-0.5 min-w-0" title={notes}>
          <StickyNote className="w-2.5 h-2.5 text-slate-400 flex-shrink-0" />
          <span className="text-[9px] italic text-slate-500 truncate" data-testid={`card-notes-${shift.id}`}>{notes}</span>
        </div>
      )}
    </>
  );
}

// Compact shift card used inside week / month grids. Pure UI — receives shift + handlers as props.
export function CalendarShiftCard({ shift, onClick, compact, draggable, onDragStart }) {
  const style = getStatusStyle(shift.status);
  if (shift._continuation) {
    return (
      <div
        className={`rounded-md border border-dashed px-2 py-1 cursor-pointer transition-all hover:shadow-md ${style.bg} mb-1 opacity-80`}
        onClick={() => onClick(shift)}
        title={`Continues from ${shift.date} ${shift.start_time} — ends ${shift.end_time}`}
        data-testid={`shift-card-cont-${shift.id}`}
      >
        <div className="flex items-center gap-1.5">
          <MoonStar className="w-2.5 h-2.5 text-slate-500 flex-shrink-0" />
          <span className={`text-[11px] font-semibold truncate ${style.text}`}>↳ until {shift.end_time}</span>
          <span className="ml-auto text-[9px] text-slate-500">from {shift.start_time}</span>
        </div>
      </div>
    );
  }
  return (
    <div
      className={`rounded-md border px-2 py-1.5 cursor-pointer transition-all hover:shadow-md ${style.bg} mb-1 ${draggable ? 'active:opacity-60' : ''}`}
      onClick={() => onClick(shift)}
      draggable={draggable}
      onDragStart={onDragStart ? (e) => onDragStart(e, shift) : undefined}
      data-testid={`shift-card-${shift.id}`}
    >
      <div className="flex items-center gap-1.5 mb-0.5">
        {draggable && <GripVertical className="w-2.5 h-2.5 text-slate-400 flex-shrink-0 cursor-grab" />}
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${style.dot}`} />
        <span className={`text-[11px] font-semibold whitespace-nowrap ${style.text}`} data-testid={`card-time-${shift.id}`}>{shift.start_time}–{shift.end_time}</span>
      </div>
      {/* client#3: time, staff and notes come first; period badge and indicators below */}
      <CardStaffNotes shift={shift} />
      <div className="mt-0.5"><ShiftPeriodBadgeForShift shift={shift} /></div>
      <CardIndicators shift={shift} onGroup={onClick} dense={compact} />
      {!compact && (
        <>
          <p className="text-[10px] font-medium truncate text-slate-800">{shift.participant_count > 1 ? `${shift.participant_count} participants` : (shift.participant_name || 'No participant')}</p>
          {/* client#14: no service / line item / agreement number on board cards — it lives in the shift drawer */}
          {shift.unfilled && !shift.shared_group_id && <p className="text-[9px] font-semibold text-amber-700" data-testid="shift-card-unfilled">Unfilled</p>}
        </>
      )}
    </div>
  );
}
